/**
 * The books a voucher can make, as the redesign's voucher pages describe them (Codex, W1
 * Round 03, kept in W3 Trust Round 09), and what this tab remembers between the voucher pages.
 */
export const voucherFormats = [
  {
    id: "story",
    name: "My Story",
    line: "A life, in your own words.",
    description:
      "The places, people and turning points that made you who you are—brought together in a book to keep and share.",
    bring: "Start with a memory, a photograph or a story you’ve always wanted to tell.",
    making:
      "Follow guided questions to gather your memories, then review the chapters and details of your life story.",
  },
  {
    id: "love",
    name: "Our Story",
    line: "The life you’ve built together.",
    description:
      "Your first meeting, everyday rituals and milestones, told by one of you through the moments that mean the most.",
    bring: "Bring memories from your life together and any photographs you’d like to include.",
    making:
      "One of you tells the story. Guided questions help shape the memories into chapters you can review.",
  },
  {
    id: "children",
    name: "A Bedtime Story",
    line: "A little wonder. A familiar heart.",
    description:
      "Turn a treasured memory into an illustrated story to read together. Familiar people and moments become a new bedtime favorite.",
    bring: "Begin with the memory you want to share, who is in it and who will be reading.",
    making:
      "Shape the story, choose its look and review the words and pictures before approving the book.",
  },
  {
    id: "recipes",
    name: "The Family Cookbook",
    line: "Keep the recipes. And the stories.",
    description:
      "The dishes everyone asks for, the handwriting on a recipe card and the people behind them—all with a place at your table.",
    bring:
      "Gather recipes, ingredients, methods and the memories or photographs that belong with them.",
    making:
      "Build your collection recipe by recipe. Review the ingredients, cooking instructions and pages as you go.",
  },
  {
    id: "comic",
    name: "The Comic Book",
    line: "Your memories, with a little more action.",
    description:
      "Bring a real moment to life in panels, pictures, captions and speech bubbles. A different way to tell a story only you can tell.",
    bring: "Choose a memory, its characters and the moments you want to see unfold.",
    making:
      "Plan the story, explore its visual direction and review the panels, words and picture consistency.",
  },
] as const;
export type VoucherFormat = (typeof voucherFormats)[number];
export type FormatId = VoucherFormat["id"];

export const isFormatId = (value: unknown): value is FormatId =>
  voucherFormats.some((format) => format.id === value);
export const formatFor = (id: string) => voucherFormats.find((format) => format.id === id);

/** A book chosen on Start a book, carried to the voucher. It never starts a book by itself. */
export type StartPlan = { edition: FormatId; name: string };
const START_KEY = "lc-w1-start-plan";

export function readStartPlan(): StartPlan | null {
  try {
    const plan = JSON.parse(sessionStorage.getItem(START_KEY) || "null") as Partial<StartPlan>;
    return plan &&
      isFormatId(plan.edition) &&
      typeof plan.name === "string" &&
      plan.name.trim().length > 0 &&
      plan.name.length <= 100
      ? { edition: plan.edition, name: plan.name }
      : null;
  } catch {
    return null;
  }
}
export function saveStartPlan(plan: StartPlan) {
  try {
    sessionStorage.setItem(START_KEY, JSON.stringify(plan));
    return true;
  } catch {
    return false;
  }
}
export function clearStartPlan() {
  try {
    sessionStorage.removeItem(START_KEY);
  } catch {
    // Nothing to clear without storage.
  }
}

/** The voucher check's answer, as the page uses it. */
export type Challenge = {
  challengeToken: string;
  expiresAt: string;
  offerName: string;
  test: boolean;
  kind?: "SHARED_TEST_GIFT" | "VOUCHER";
  reusable?: boolean;
  selectionRequired?: boolean;
  /** The one book a fixed voucher makes. */
  templateId?: string;
  /** Absent from a server older than this page: then the page doesn't say. */
  includedPrintCopies?: 0 | 1;
  allowedTemplates?: Array<{ templateId: string; name: string }>;
};

export type VoucherStage = "shelf" | "details" | "confirm";
/** Who will tell the story: the person using the voucher, or someone they're giving it to. */
export type Teller = "self" | "gift";
/**
 * A checked voucher, kept in this tab until its check expires, so leaving to read an example
 * and coming back carries on. The discovery pages read the same key to offer "Continue your
 * voucher".
 */
export type VoucherSession = {
  stage: VoucherStage;
  edition: FormatId;
  name: string;
  challenge: Challenge;
  /** The cross-tab sign-in draft saved for this voucher, if one was. */
  draftId?: string;
  /** Who will tell the story, once that's been said. */
  teller?: Teller;
};
const VOUCHER_KEY = "lc-w1-voucher-bookshop";

export function readVoucherSession(now = Date.now()): VoucherSession | null {
  try {
    const saved = JSON.parse(
      sessionStorage.getItem(VOUCHER_KEY) || "null",
    ) as Partial<VoucherSession> | null;
    const challenge = saved?.challenge;
    if (
      !saved ||
      !["shelf", "details", "confirm"].includes(saved.stage as string) ||
      !isFormatId(saved.edition) ||
      typeof saved.name !== "string" ||
      saved.name.length > 100 ||
      !challenge ||
      typeof challenge.challengeToken !== "string" ||
      !/^[A-Za-z0-9_-]{43}$/u.test(challenge.challengeToken) ||
      typeof challenge.offerName !== "string" ||
      (saved.draftId !== undefined && typeof saved.draftId !== "string") ||
      (saved.teller !== undefined && saved.teller !== "self" && saved.teller !== "gift") ||
      !(Date.parse(challenge.expiresAt) > now)
    ) {
      if (saved) clearVoucherSession();
      return null;
    }
    return saved as VoucherSession;
  } catch {
    return null;
  }
}
/**
 * Where "Start a book" leads (C3, 2026-11-02): a voucher already checked in this tab and not yet
 * used goes on to choosing its book; otherwise the books, which say each one starts with a voucher.
 * It never leads to the page it's on: from the books themselves it goes to the voucher (review
 * P2-d).
 */
export function startBookHref(currentPath?: string): "/redeem" | "/books" {
  const target = readVoucherSession() ? "/redeem" : "/books";
  const here = currentPath?.replace(/\/+$/u, "").toLowerCase() || "/";
  return currentPath !== undefined && here === target ? "/redeem" : target;
}
export function saveVoucherSession(session: VoucherSession) {
  try {
    sessionStorage.setItem(VOUCHER_KEY, JSON.stringify(session));
    return true;
  } catch {
    return false;
  }
}
export function clearVoucherSession() {
  try {
    sessionStorage.removeItem(VOUCHER_KEY);
  } catch {
    // Nothing to clear without storage.
  }
}

/** The books this voucher lets the buyer choose from, in the collection's order. */
export function includedFormats(challenge: Challenge): VoucherFormat[] {
  const ids =
    challenge.allowedTemplates?.map(({ templateId }) => templateId) ??
    (challenge.templateId ? [challenge.templateId] : []);
  return voucherFormats.filter((format) => ids.includes(format.id));
}

/** What the voucher says about print: included, not included, or not said by the server. */
export function printEligibility(
  challenge: Challenge,
): "included" | "practice" | "digital" | "unverified" {
  if (challenge.kind === "SHARED_TEST_GIFT") return "practice";
  if (challenge.includedPrintCopies === 1) return "included";
  if (challenge.includedPrintCopies === 0) return "digital";
  return "unverified";
}
